import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChatList, MessageList, MessageInput } from '@/components/chat';
import { apiService, wsService, authService } from '@/services';
import type { Chat, Message, User } from '@/types';

const ChatPage: React.FC = () => {
  const [activeChat, setActiveChat] = useState<Chat | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const queryClient = useQueryClient();

  // Get current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await authService.getCurrentUser();
        setCurrentUser(user);
      } catch (error) {
        console.error('Failed to get current user:', error);
      }
    };

    fetchUser();
  }, []);

  // Connect to WebSocket
  useEffect(() => {
    if (currentUser) {
      wsService.connect().catch(error => {
        console.error('Failed to connect to WebSocket:', error);
      });

      return () => {
        wsService.disconnect();
      };
    }
  }, [currentUser]);

  // Subscribe to chat messages when active chat changes
  useEffect(() => {
    if (activeChat && wsService.isConnected()) {
      wsService.subscribeToChat(activeChat.id, (message: Message) => {
        // Update messages cache
        queryClient.setQueryData(
          ['messages', activeChat.id],
          (oldData: any) => {
            if (!oldData) return { data: [message], pagination: { hasMore: false } };
            return {
              ...oldData,
              data: [...oldData.data, message]
            };
          }
        );

        // Update chat list to reflect new message
        queryClient.invalidateQueries({ queryKey: ['chats'] });
      });

      return () => {
        wsService.unsubscribeFromChat(activeChat.id);
      };
    }
  }, [activeChat, queryClient]);

  // Fetch chats
  const { data: chatsData, isLoading: chatsLoading, refetch: refetchChats } = useQuery({
    queryKey: ['chats'],
    queryFn: async () => {
      const response = await apiService.getChats();
      if (response.error) throw new Error(response.error);
      return response.data || [];
    },
    enabled: !!currentUser,
  });

  // Fetch messages for active chat
  const { data: messagesData, isLoading: messagesLoading } = useQuery({
    queryKey: ['messages', activeChat?.id],
    queryFn: async () => {
      if (!activeChat) return { data: [], pagination: { hasMore: false } };
      const response = await apiService.getMessages(activeChat.id);
      if (response.error) throw new Error(response.error);
      return response.data || { data: [], pagination: { hasMore: false } };
    },
    enabled: !!activeChat,
  });

  // Fetch users
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await apiService.getUsers();
      if (response.error) throw new Error(response.error);
      return response.data || [];
    },
    enabled: !!currentUser,
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async ({ chatId, content }: { chatId: string; content: string }) => {
      const response = await apiService.sendMessage(chatId, content);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (message, { chatId }) => {
      // Optimistically update messages
      queryClient.setQueryData(['messages', chatId], (oldData: any) => {
        if (!oldData) return { data: [message], pagination: { hasMore: false } };
        return {
          ...oldData,
          data: [...oldData.data, message]
        };
      });

      // Update chat list
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
  });

  const handleChatSelect = (chat: Chat) => {
    setActiveChat(chat);
  };

  const handleSendMessage = async (content: string) => {
    if (!activeChat || !currentUser) return;

    try {
      await sendMessageMutation.mutateAsync({
        chatId: activeChat.id,
        content,
      });
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error; // Re-throw to show error in UI
    }
  };

  const handleSignOut = async () => {
    try {
      await wsService.disconnect();
      await authService.signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const chats = chatsData || [];
  const messages = messagesData?.data || [];
  const users = usersData || [];

  return (
    <div className="h-screen flex bg-gray-100">
      {/* Sidebar - Chat List */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {currentUser?.username.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Chat App</h1>
                <p className="text-sm text-gray-500">{currentUser?.username}</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="btn-ghost p-2 rounded-md text-gray-500 hover:text-gray-700"
              title="Sign Out"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-hidden">
          <ChatList
            chats={chats}
            activeChat={activeChat || undefined}
            isLoading={chatsLoading}
            onChatSelect={handleChatSelect}
            onRefresh={refetchChats}
          />
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {activeChat ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 font-medium">
                    {activeChat.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{activeChat.name}</h2>
                  <p className="text-sm text-gray-500">
                    {activeChat.participants.length} participant{activeChat.participants.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <MessageList
              messages={messages}
              currentUserId={currentUser?.id || ''}
              users={users}
              isLoading={messagesLoading}
              className="flex-1"
            />

            {/* Message Input */}
            <MessageInput
              onSendMessage={handleSendMessage}
              isLoading={sendMessageMutation.isPending}
              onTypingStart={() => {
                if (wsService.isConnected()) {
                  wsService.sendTypingIndicator(activeChat.id, true);
                }
              }}
              onTypingStop={() => {
                if (wsService.isConnected()) {
                  wsService.sendTypingIndicator(activeChat.id, false);
                }
              }}
            />
          </>
        ) : (
          /* No Chat Selected */
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Welcome to Chat App</h3>
              <p className="text-gray-500">Select a chat from the sidebar to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatPage;